import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FiArrowLeft, FiLoader, FiCpu } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { jobApi, resumeApi } from '../../services/api';
import { ProgressBar } from '../../components/common/ProgressBar';
import { DisabledCreditButton } from '../../components/membership';
import CreditCostNotification from '../../components/membership/CreditCostNotification';
import { useMembership } from '../../contexts/MembershipContext';
import { getResumeContentSafe, getJobContentSafe } from '../../utils/resumeContentHelper';
import PageHeader from '../../components/layout/PageHeader';
import { pageDescriptions } from '../../data/pageDescriptions';
import sanitizeHtml from '../../utils/sanitizeHtml';
import '../../styles/analysis.css';

const JobMatchAnalysis = () => {
  const { jobId } = useParams();
  const { refreshCreditBalance } = useMembership();
  
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [job, setJob] = useState(null);
  const [resumes, setResumes] = useState([]);
  const [resumeVersions, setResumeVersions] = useState([]);
  const [selectedResume, setSelectedResume] = useState('');
  const [selectedVersion, setSelectedVersion] = useState('');
  const [analysisHtml, setAnalysisHtml] = useState(null);
  const [analysisCreditCost, setAnalysisCreditCost] = useState(5);
  const didAutoRunRef = useRef(false);

  useEffect(() => {
    didAutoRunRef.current = false;
  }, [jobId]);
  
  useEffect(() => {
    fetchData();
  }, [jobId]);
  
  useEffect(() => {
    if (selectedResume) {
      loadResumeVersions(selectedResume);
    }
  }, [selectedResume]);
  

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch job and resumes
      const [jobResponse, resumesResponse] = await Promise.all([
        jobApi.getJobById(jobId),
        resumeApi.getAllResumes()
      ]);
      
      setJob(jobResponse.data);
      setResumes(resumesResponse.data);
      
      if (resumesResponse.data.length > 0) {
        setSelectedResume(resumesResponse.data[0].id);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load job data');
    } finally {
      setLoading(false);
    }
  };
  
  const loadResumeVersions = async (resumeId) => {
    try {
      const response = await resumeApi.getResumeById(resumeId);
      const { versions } = response.data;
      setResumeVersions(versions);
      
      if (versions.length > 0) {
        // Select latest version by default
        setSelectedVersion(versions[versions.length - 1].versionId);
      }
    } catch (error) {
      console.error('Error loading resume versions:', error);
      toast.error('Failed to load resume versions');
    }
  };
  
  const handleResumeChange = (e) => {
    setSelectedResume(e.target.value);
    setAnalysisHtml(null); // Reset analysis when resume changes
  };
  
  const handleVersionChange = (e) => {
    setSelectedVersion(e.target.value);
    setAnalysisHtml(null); // Reset analysis when version changes
  };

  const runMatchAnalysis = useCallback(async (isAutoRun = false) => {
    if (!selectedResume || !selectedVersion) {
      if (!isAutoRun) toast.error('Please select a resume and version');
      return;
    }
    
    let progressInterval;
    try {
      setAnalyzing(true);
      setAnalysisProgress(0);
      
      progressInterval = setInterval(() => {
        setAnalysisProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 500);
      
      const resumeContent = await getResumeContentSafe(selectedResume, selectedVersion);
      const jobContent = await getJobContentSafe(jobId);
      
      const response = await jobApi.analyzeJobMatch(
        jobId, 
        selectedResume, 
        selectedVersion,
        resumeContent,
        jobContent,
        { forceRefresh: false, cost: analysisCreditCost }
      );
      
      setAnalysisHtml(response.data.htmlAnalysis);
      
      await refreshCreditBalance();
      
      if (progressInterval) clearInterval(progressInterval);
      setAnalysisProgress(100);

      const cached = response.data?.cached === true;
      if (!(isAutoRun && cached)) {
        toast.success(cached ? 'Loaded saved analysis' : 'Analysis completed');
      }
    } catch (error) {
      if (progressInterval) clearInterval(progressInterval);
      console.error('Error analyzing match:', error);
      if (error.response && error.response.status === 402) {
        toast.error('Not enough credits. Please upgrade your plan or purchase more credits.');
      } else {
        toast.error('Failed to analyze job match');
      }
    } finally {
      if (progressInterval) clearInterval(progressInterval);
      setTimeout(() => {
        setAnalyzing(false);
        setAnalysisProgress(0);
      }, 500);
    }
  }, [
    jobId,
    selectedResume,
    selectedVersion,
    analysisCreditCost,
    refreshCreditBalance,
  ]);

  useEffect(() => {
    if (loading || analyzing || !job || !selectedResume || !selectedVersion || didAutoRunRef.current) {
      return;
    }
    didAutoRunRef.current = true;
    runMatchAnalysis(true);
  }, [loading, analyzing, job, selectedResume, selectedVersion, runMatchAnalysis]);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }
  
  const copy = pageDescriptions['/jobs/:jobId/match'];

  return (
    <div className="max-w-6xl mx-auto">
      <PageHeader
        title={`${copy.title}: ${job?.title || 'Job'}`}
        description={copy.description}
        whatYouCanDo={copy.whatYouCanDo}
        whatYouShouldDo={copy.whatYouShouldDo}
        actions={
          <Link
            to={`/jobs/${jobId}`}
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
          >
            <FiArrowLeft className="mr-2 h-4 w-4" />
            Back to Job Details
          </Link>
        }
      />
      <div className="card overflow-hidden mb-8">
        <div className="px-4 py-5 sm:p-6">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label htmlFor="resume" className="block text-sm font-medium text-gray-700">
                Select Resume
              </label>
              <select
                id="resume"
                name="resume"
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                value={selectedResume}
                onChange={handleResumeChange}
              >
                <option value="">Select a Resume</option>
                {resumes.map(resume => (
                  <option key={resume.id} value={resume.id}>
                    {resume.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label htmlFor="version" className="block text-sm font-medium text-gray-700">
                Select Version
              </label>
              <select
                id="version"
                name="version"
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                value={selectedVersion}
                onChange={handleVersionChange}
                disabled={!selectedResume}
              >
                <option value="">Select a Version</option>
                {resumeVersions.map(version => (
                  <option key={version.versionId} value={version.versionId}>
                    {version.customName || version.versionId} ({version.versionType})
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="space-y-4">
            <CreditCostNotification serviceCost={analysisCreditCost} serviceName="job match analysis" />
            
            <div className="flex justify-center">
              <DisabledCreditButton
                serviceType="job_matching"
                onClick={() => runMatchAnalysis(false)}
                disabled={analyzing || !selectedResume || !selectedVersion}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                {analyzing ? (
                  <>
                    <FiLoader className="animate-spin -ml-1 mr-2 h-4 w-4" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <FiCpu className="-ml-1 mr-2 h-4 w-4" />
                    Analyze Match ({analysisCreditCost} credits)
                  </>
                )}
              </DisabledCreditButton>
            </div>
          </div>
        </div>
      </div>
      
      {/* Progress Bar */}
      {analyzing && (
        <ProgressBar progress={analysisProgress} className="mb-6" />
      )}

      {/* Analysis Results Section */}
      {analysisHtml && (
        <div className="card overflow-hidden mb-8">
          <div className="px-4 py-5 sm:p-6">
            <div 
              className="analysis-results p-2" 
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(analysisHtml) }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default JobMatchAnalysis;
